/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Grid,
  Image,
  Text,
  useDisclosure,
  useToast,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';
import { useMutation } from 'react-query';
import React from 'react';
import { removeAccount } from '@/service/club/api';
import { useProfile } from '@/contexts/ProfileProvider';
import { IProfileForm } from '@/service/types';
import { masks } from '@/utils/masks';
import { useTheme } from '@/contexts/ThemeProvider';
import { OrderHistoryPage } from '@/pages/OrderHistory/OrderHistory';
import { Referral } from '@/pages/Profile/components/Referral';
import { Econometro } from '@/pages/Profile/components/Econometro';
import { EconometroList } from '@/pages/Profile/components/EconometroList';

function RemoveAccountModal() {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { logout } = useProfile();
  const cancelRef: any = React.useRef(null);

  const removeMutation = useMutation(() => removeAccount(), {
    onSuccess() {
      logout();
    },
    onError() {
      toast({
        title: 'Não foi possível apagar seus dados.',
        description:
          'Entre em contato conosco pelo email suporte@partiu.com.br para pedir sua remoção de dados. O prazo máximo é de cinco dias úteis.',
      });
    },
  });

  return (
    <>
      <AlertDialog leastDestructiveRef={cancelRef} isOpen={isOpen} onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent margin="20px">
            <AlertDialogHeader>Tem certeza?</AlertDialogHeader>
            <AlertDialogCloseButton />
            <AlertDialogBody>
              Essa ação apagará todos os dados relacionados a sua conta em nosso banco de dados, inclusive seu acesso
              à plataforma. Caso recrie sua conta, você não terá mais acesso e deverá consultar a reativação com
              a sua empresa.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                variant="outline"
                color={'#5e5e5b'}
                backgroundColor="white"
                borderColor="primary"
                onClick={onClose}
              >
                Cancelar
              </Button>
              <Button onClick={() => removeMutation.mutate()} ml={3}>
                Deletar meus Dados
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <Button
        color={'#5e5e5b'}
        borderColor="primary"
        variant="ghost"
        width={'fit-content'}
        onClick={onOpen}
        margin={'auto'}
        backgroundColor={'transparent'}
      >
        Quero apagar minha conta
      </Button>
    </>
  );
}

export function ProfileForm() {
  const { name, colors, referralModule, product } = useTheme();
  const econometerModule = product?.metadata?.econometermodule?.value === 'true';
  const { profile, updateProfile, isLoading } = useProfile();

  const formSchema = z.object({
    full_name: z.string().min(1),
    telephone: z.string().length(15),
    email: z.string().email(),
    cpf: z.string().length(14),
  });

  const methods = useForm<IProfileForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      full_name: profile?.fullName,
      telephone: profile?.telephone ? masks.cell(profile.telephone) : '',
      email: profile?.email,
      cpf: profile?.cpf ? masks.cpf(profile.cpf) : '',
    },
  });

  const { control, handleSubmit } = methods;

  function doUpdateProfile(data: IProfileForm) {
    updateProfile({ full_name: data.full_name, telephone: masks.unmask(data.telephone) });
  }

  return (
    <Grid width={'100%'} templateColumns={{ base: '1fr', md: '30% 1fr' }} gap={{ md: 10 }} alignItems="start">
      <Box
        display={{
          base: 'none',
          md: 'flex',
        }}
        alignItems={'flex-start'}
        justifyContent={'center'}
        pt={2}
      >
        <Image width="90%" src="/assets/side-image.png" />
      </Box>
      <Box width={'100%'}>
        <Tabs variant="unstyled" width="100%">
          <TabList borderBottom={`2px solid ${colors.primaryColor}`} bg="#f1f4f5" borderRadius="lg" px={2} py={1}>
            <Tab
              fontWeight="bold"
              fontSize="16px"
              color={colors.primaryColor}
              _selected={{
                color: 'white',
                bg: colors.primaryColor,
                borderRadius: 'md',
                boxShadow: '0 2px 8px #0002',
              }}
              px={6}
              py={2}
              transition="all 0.2s"
            >
              Meus Dados
            </Tab>
            {econometerModule && (
              <Tab
                fontWeight="bold"
                fontSize="16px"
                color={colors.primaryColor}
                _selected={{
                  color: 'white',
                  bg: colors.primaryColor,
                  borderRadius: 'md',
                  boxShadow: '0 2px 8px #0002',
                }}
                px={6}
                py={2}
                transition="all 0.2s"
              >
                Econômetro
              </Tab>
            )}
            <Tab
              fontWeight="bold"
              fontSize="16px"
              color={colors.primaryColor}
              _selected={{
                color: 'white',
                bg: colors.primaryColor,
                borderRadius: 'md',
                boxShadow: '0 2px 8px #0002',
              }}
              px={6}
              py={2}
              transition="all 0.2s"
            >
              Minhas Compras
            </Tab>
            {referralModule && (
              <Tab
                fontWeight="bold"
                fontSize="16px"
                color={colors.primaryColor}
                _selected={{
                  color: 'white',
                  bg: colors.primaryColor,
                  borderRadius: 'md',
                  boxShadow: '0 2px 8px #0002',
                }}
                px={6}
                py={2}
                transition="all 0.2s"
              >
                Indicações
              </Tab>
            )}
          </TabList>
          <TabPanels>
            <TabPanel px={0} py={6}>
              <VStack spacing={5} align={'flex-start'} width={'100%'}>
                <Text fontSize={'14px'}>
                  Verifique aqui todos seus dados pessoais vinculados à {name}. Alguns campos não podem ser editados.
                </Text>
                <form onSubmit={handleSubmit(doUpdateProfile)} style={{ width: '100%' }}>
                  <VStack spacing={5} w={'100%'}>
                    <Input name="full_name" control={control} label="Nome Completo" />
                    <Input disabled name="email" control={control} label="Email" />
                    <Input disabled={!!profile?.cpf} name="cpf" mask="cpf" control={control} label="CPF" />
                    <Input name="telephone" mask="cell" control={control} label="Celular" />
                    <Button isLoading={isLoading} type="submit" marginTop={'10px'}>
                      Atualizar
                    </Button>
                  </VStack>
                </form>
                <RemoveAccountModal />
              </VStack>
            </TabPanel>
            {econometerModule && (
              <TabPanel px={0} py={6}>
                <VStack width="100%" spacing={4} align="stretch">
                  <Econometro />
                  <EconometroList />
                </VStack>
              </TabPanel>
            )}
            <TabPanel px={0}>
              <Box width="100%">
                <OrderHistoryPage />
              </Box>
            </TabPanel>
            {referralModule && (
              <TabPanel px={0} py={6}>
                <Referral />
              </TabPanel>
            )}
          </TabPanels>
        </Tabs>
      </Box>
    </Grid>
  );
}
